/**
 * <s-offer-picker> — bundle-first product chooser inside a product form.
 *
 * Writes the variant matching the chosen card and its option pills into the form's
 * input[name="id"], and relabels the form's add buttons (including a sticky bar that
 * targets the form with form="<id>") for pre-order vs in-stock vs sold out.
 *
 * Required markup:
 *   [data-offer]                       — one card per product; data-preorder="true|false"
 *   input[data-offer-radio]            — the card's selector radio
 *   input[data-option-index]           — option pill radios; value is the option value name
 *   script[data-offer-variants]        — JSON [{ id, available, options: [] }]
 * Attributes: data-preorder-label, data-add-label, data-sold-out-label
 */
class SOfferPicker extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('change', () => this.#sync(), { signal: this.#ac.signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #sync() {
    const form = this.closest('form');
    const card = this.querySelector('[data-offer-radio]:checked')?.closest('[data-offer]');
    if (!form || !card) return;

    let variants = [];
    try {
      variants = JSON.parse(card.querySelector('[data-offer-variants]')?.textContent || '[]');
    } catch {
      variants = [];
    }

    const picked = [];
    card.querySelectorAll('input[data-option-index]:checked').forEach((input) => {
      picked[Number(input.dataset.optionIndex)] = input.value;
    });

    const matches = (variant) => picked.every((value, index) => variant.options[index] === value);
    const variant = variants.find(matches) || variants.find((v) => v.available) || variants[0];
    if (!variant) return;

    const idInput = form.querySelector('input[name="id"]');
    if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id);

    let label = this.dataset.addLabel || '';
    if (!variant.available) label = this.dataset.soldOutLabel || label;
    else if (card.dataset.preorder === 'true') label = this.dataset.preorderLabel || label;

    const buttons = [
      ...form.querySelectorAll('button[name="add"]'),
      ...(form.id ? document.querySelectorAll(`button[form="${form.id}"][name="add"]`) : []),
    ];
    buttons.forEach((button) => {
      button.textContent = label;
      button.disabled = !variant.available;
    });
  }
}

if (!customElements.get('s-offer-picker')) {
  customElements.define('s-offer-picker', SOfferPicker);
}
